import { useEffect, useMemo, useState } from 'react';

import { ReadinessEvidenceDrawer } from '@/features/Readiness';
import type { EvidenceStatusFilter } from '@/features/Readiness';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useOrgReadinessComponentEvidence } from '@/lib/hooks/useOrg';
import type { ReadinessEvidenceItem, ReadinessEvidenceStatus } from '@/lib/api/readiness';

export interface StudentEvidenceDrawerProps {
  batchId: string;
  userId: string;
  /** The component being drilled into; `null` closes the drawer. */
  target: { key: string; label: string } | null;
  onClose: () => void;
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  onNavigate?: (href: string) => void;
}

/**
 * Wires the readiness drill-down drawer to the paged evidence endpoint.
 *
 * All of the fetching lives here so the drawer itself stays presentational —
 * and so the search term can be debounced in one place. The filters are part
 * of the query key, so switching status or typing starts a fresh list rather
 * than appending unrelated rows to the one on screen.
 */
export function StudentEvidenceDrawer({
  batchId,
  userId,
  target,
  onClose,
  linkFor,
  onNavigate,
}: StudentEvidenceDrawerProps) {
  const [status, setStatus] = useState<EvidenceStatusFilter>('all');
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query.trim(), 250);

  // A new component is a new question: carrying the last one's search term
  // into it would show an empty list for reasons the user can't see.
  useEffect(() => {
    if (!target) return;
    setStatus('all');
    setQuery('');
  }, [target?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data,
    isLoading,
    isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useOrgReadinessComponentEvidence(
    batchId,
    userId,
    target?.key,
    {
      status: status === 'all' ? undefined : (status as ReadinessEvidenceStatus),
      q: debouncedQuery || undefined,
    },
    { enabled: !!target },
  );

  const pages = data?.pages ?? [];
  const items = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  const first = pages[0];

  return (
    <ReadinessEvidenceDrawer
      open={!!target}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={target?.label ?? ''}
      summary={first?.summary}
      counts={first?.counts}
      items={items}
      total={first?.total ?? 0}
      // A settling search term is still "loading the first page" as far as the
      // reader is concerned; showing stale rows under a new query would lie.
      loading={isLoading || query.trim() !== debouncedQuery}
      loadingMore={isFetchingNextPage}
      error={isError}
      onRetry={() => void refetch()}
      status={status}
      onStatusChange={setStatus}
      query={query}
      onQueryChange={setQuery}
      hasMore={!!hasNextPage}
      onLoadMore={() => void fetchNextPage()}
      linkFor={linkFor}
      onNavigate={onNavigate}
    />
  );
}
